import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../context/ThemeContext';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { Logger } from '../../utils/logger';

const log = Logger.create('PageHeader');

/**
 * PageHeader - Einheitlicher Header für alle Views
 * 
 * Props:
 * - title: string - Haupttitel
 * - subtitle: string | ReactNode - Untertitel (optional)
 * - onBack: function | string - Zurück-Handler oder Route (optional)
 * - backLabel: string - Text für Zurück-Button (default: "← Zurück")
 * - centerContent: ReactNode - Content in der Mitte des Headers (optional)
 * - tabs: Array<{id, label, icon?}> - Tab-Konfiguration (optional)
 * - activeTab: string - Aktiver Tab-ID (optional)
 * - onTabChange: function - Tab-Wechsel Handler (optional)
 * - actions: Array<{label, onClick, variant?, icon?}> - Action-Buttons (optional)
 * - primaryAction: {label, onClick, icon?, disabled?} - Primäre Aktion rechts (optional)
 * - showLogout: boolean - Zeige Abmelden-Button (default: false)
 * - maxWidth: string - Container max-width (default: "max-w-7xl")
 * - children: ReactNode - Zusätzlicher Content im Header (optional)
 */
function PageHeader({
  title,
  subtitle,
  onBack,
  backLabel = '← Zurück',
  centerContent,
  tabs,
  activeTab,
  onTabChange,
  actions = [],
  primaryAction,
  showLogout = false,
  maxWidth = 'max-w-7xl',
  children
}) {
  const navigate = useNavigate();
  const { logout } = useUnifiedAuth();

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    } else if (typeof onBack === 'string') {
      navigate(onBack);
    } else {
      navigate(-1);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      log.error('Logout error', err);
    }
  };

  // Button-Styles
  const secondaryButtonClass = "px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition";
  const primaryButtonClass = "px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg theme-transition";
  const successButtonClass = "px-4 py-2 text-sm font-medium text-white bg-success-600 hover:bg-success-700 rounded-lg theme-transition";
  const dangerButtonClass = "px-4 py-2 text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 rounded-lg theme-transition";

  const getButtonClass = (variant) => {
    switch (variant) {
      case 'primary': return primaryButtonClass;
      case 'success': return successButtonClass;
      case 'danger': return dangerButtonClass;
      default: return secondaryButtonClass;
    }
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20 theme-transition">
      <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-4`}>
        <div className="flex items-center justify-between">
          {/* Left: Back + Title */}
          <div className="flex items-center gap-4">
            {onBack !== undefined && (
              <button
                onClick={handleBack}
                className={secondaryButtonClass}
                title="Zurück"
              >
                {backLabel}
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h1>
              {subtitle && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Center: Optional Content (e.g., WorkflowSelector) */}
          {centerContent && (
            <div className="hidden md:flex">
              {centerContent}
            </div>
          )}

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            <ThemeToggle />
            
            {/* Secondary Actions */}
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                className={getButtonClass(action.variant)}
                disabled={action.disabled}
                title={action.title}
              >
                {action.icon && <span className={action.label ? "mr-1" : ""}>{action.icon}</span>}
                {action.label}
              </button>
            ))}

            {/* Primary Action */}
            {primaryAction && (
              <button
                onClick={primaryAction.onClick}
                className={getButtonClass(primaryAction.variant || 'primary')}
                disabled={primaryAction.disabled}
              >
                {primaryAction.icon && <span className={primaryAction.label ? "mr-1" : ""}>{primaryAction.icon}</span>}
                {primaryAction.label}
              </button>
            )}

            {/* Logout */}
            {showLogout && (
              <button
                onClick={handleLogout}
                className={secondaryButtonClass}
              >
                Abmelden
              </button>
            )}
          </div>
        </div>

        {/* Additional Content (e.g., metadata fields) */}
        {children && (
          <div className="mt-4">
            {children}
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      {tabs && tabs.length > 0 && (
        <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange?.(tab.id)}
                className={`px-6 py-3 font-medium text-sm theme-transition border-b-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {tab.icon && <span className="mr-1">{tab.icon}</span>}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export default PageHeader;
